// src/hooks/useBookings.js - COMPLETE 882-LINE IMPLEMENTATION with SURGICAL FIXES
import { useCallback, useEffect, useState } from 'react';
import { BookingService } from '../services/BookingService';
import { CalendarSyncService } from '../services/CalendarSyncService';
import { ConflictResolutionService } from '../services/ConflictResolutionService';
import { FirebaseService } from '../services/FirebaseService';
import { useAuth } from './useAuth';

const LISA_AGENT_API = 'https://lisa-agent-652532757120.us-central1.run.app';

export const useBookings = (businessId) => {
  const { user } = useAuth();
  const effectiveBusinessId = businessId || user?.businessId;

  // ==========================================
  // STATE MANAGEMENT - PRESERVED
  // ==========================================
  const [bookings, setBookings] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarIntegration, setCalendarIntegration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ==========================================
  // SURGICAL FIX 1: Add sync deduplication tracking
  // ==========================================
  const [recentSyncs, setRecentSyncs] = useState(new Set());

  // ==========================================
  // SURGICAL FIX 2: Force update trigger for real-time updates
  // ==========================================
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const forceUpdate = useCallback(() => {
    setUpdateTrigger(prev => prev + 1);
    console.log('🔄 Forcing component updates for booking changes');
  }, []);

  // ==========================================
  // SURGICAL FIX 4: Safe date/time parsing utility with Timezone Consistency
  // ==========================================
  const safeParseDateTime = useCallback((booking) => {
    try {
      // Handle various date field naming conventions
      const dateField = booking.booking_date || booking.bookingDate || booking.date;
      const timeField = booking.time || booking.booking_time;
      
      if (!dateField || !timeField) {
        console.warn('⚠️ Missing date/time fields in booking:', booking.id);
        return null;
      }
      
      // SURGICAL FIX: Timezone-consistent date parsing
      // Parse date components separately to avoid timezone shifts
      const dateParts = dateField.split('-');
      const timeParts = timeField.split(':');
      
      if (dateParts.length !== 3 || timeParts.length < 2) {
        console.warn('⚠️ Invalid date/time format in booking:', booking.id, dateField, timeField);
        return null;
      }
      
      // Create date using local timezone consistently
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
      const day = parseInt(dateParts[2]);
      const hours = parseInt(timeParts[0]);
      const minutes = parseInt(timeParts[1]);
      
      const dateObj = new Date(year, month, day, hours, minutes);
      
      if (isNaN(dateObj.getTime())) {
        console.warn('⚠️ Invalid date/time values in booking:', booking.id, dateField, timeField);
        return null;
      }
      
      // Validate that parsed components match input (prevents invalid dates like Feb 30)
      if (dateObj.getFullYear() !== year || 
          dateObj.getMonth() !== month || 
          dateObj.getDate() !== day ||
          dateObj.getHours() !== hours ||
          dateObj.getMinutes() !== minutes) {
        console.warn('⚠️ Date parsing mismatch in booking:', booking.id, dateField, timeField);
        return null;
      }
      
      return dateObj;
    } catch (error) {
      console.warn('⚠️ Date parsing error for booking:', booking.id, error);
      return null;
    }
  }, []);

  // ==========================================
  // SURGICAL FIX 4: Timezone-consistent date formatting utilities
  // ==========================================
  
  const formatBookingDate = useCallback((booking) => {
    const parsedDate = safeParseDateTime(booking);
    if (!parsedDate) return null;
    
    return {
      date: parsedDate.toISOString().split('T')[0], // YYYY-MM-DD
      time: `${parsedDate.getHours().toString().padStart(2, '0')}:${parsedDate.getMinutes().toString().padStart(2, '0')}`, // HH:MM
      displayDate: parsedDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      }),
      displayTime: parsedDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      fullDateTime: parsedDate
    };
  }, [safeParseDateTime]);

  // Helper to create calendar-compatible event objects with consistent dates
  const createCalendarEvent = useCallback((booking) => {
    const parsedDate = safeParseDateTime(booking);
    if (!parsedDate) return null;
    
    const duration = booking.duration_minutes || booking.durationMinutes || booking.duration || 60;
    const endTime = new Date(parsedDate.getTime() + (duration * 60000));
    
    return {
      id: booking.id || `booking_${Date.now()}_${Math.random()}`,
      title: `${booking.service_name || 'Service'} - ${booking.customer_name || 'Customer'}`,
      start: parsedDate,
      end: endTime,
      type: 'booking',
      source: 'firebase',
      booking: booking,
      status: booking.status,
      synced: booking.calendar_event_id ? true : false
    };
  }, [safeParseDateTime]);
  const processBookingsForDisplay = useCallback((rawBookings) => {
    // ADD: Safe date validation without changing data structure
    const validBookings = rawBookings.filter(booking => {
      const parsedDate = safeParseDateTime(booking);
      if (!parsedDate) {
        console.log(`⏭️ Skipping booking ${booking.id} due to invalid date/time`);
        return false;
      }
      return true;
    });
    
    // PRESERVE: Return processed bookings in existing format
    return validBookings;
  }, [safeParseDateTime]);

  // ==========================================
  // CALENDAR INTEGRATION STATUS CHECK - PRESERVED
  // ==========================================
  
  useEffect(() => {
    if (!effectiveBusinessId) return;
    
    const checkCalendarStatus = async () => {
      try {
        const integration = await FirebaseService.checkCalendarIntegration(effectiveBusinessId);
        setCalendarIntegration(integration);
        console.log('Calendar integration status:', integration?.status || 'not_setup');
      } catch (error) {
        console.warn('Calendar integration check failed:', error);
        setCalendarIntegration({ 
          hasCalendar: false, 
          status: 'not_setup',
          setupURL: '/dashboard/calendar-integration'
        });
      }
    };
    
    checkCalendarStatus();
  }, [effectiveBusinessId]);

  // ==========================================
  // SURGICAL FIX 2: Enhanced Real-time Listener with Force Updates
  // ==========================================

  useEffect(() => {
    if (!effectiveBusinessId) return;

    console.log('Setting up smart real-time booking listener for business:', effectiveBusinessId);
    
    const unsubscribe = FirebaseService.createBookingsListener(
      effectiveBusinessId, 
      (bookingData) => {
        console.log(`Real-time booking update received: ${bookingData.length} bookings`);
        
        // SURGICAL FIX 4: Apply safe data processing
        const processedBookings = processBookingsForDisplay(bookingData);
        setBookings(processedBookings);
        setLastUpdated(new Date());

        // SURGICAL FIX 2: Force component updates at the end
        forceUpdate();

        // PRESERVE: Smart calendar sync only if needed
        if (calendarIntegration?.hasCalendar && processedBookings.length > 0) {
          const unsyncedBookings = processedBookings.filter(booking => !booking.calendar_event_id);
          if (unsyncedBookings.length > 0) {
            console.log(`📋 Found ${unsyncedBookings.length} bookings needing calendar sync`);
            syncToGoogleCalendar(unsyncedBookings);
          }
        }
      },
      (error) => {
        console.error('Real-time bookings listener error:', error);
        setError(error.message);
        // SURGICAL FIX 2: Force update even on errors
        forceUpdate();
      }
    );

    return () => {
      console.log('📴 Cleaning up real-time booking listener');
      unsubscribe();
    };
  }, [effectiveBusinessId, calendarIntegration, processBookingsForDisplay, forceUpdate]); // ADD forceUpdate to dependencies

  // ==========================================
  // SURGICAL FIX 1: Enhanced syncToGoogleCalendar with Deduplication
  // ==========================================
  
  const syncToGoogleCalendar = useCallback(async (bookingData) => {
    // SURGICAL FIX 1: Filter out recently synced bookings (preserve all existing logic after this)
    const unsyncedBookings = Array.isArray(bookingData) ? bookingData : [bookingData];
    const needsSync = unsyncedBookings.filter(booking => {
      const bookingId = booking.id;
      
      // Skip if synced within last 10 seconds (prevent duplicate triggers)
      if (recentSyncs.has(bookingId)) {
        console.log(`⏭️ Skipping duplicate sync for booking ${bookingId}`);
        return false;
      }
      
      // Skip if already has calendar event ID (preserve existing check)
      if (booking.calendar_event_id) {
        console.log(`✅ Booking ${bookingId} already synced to calendar`);
        return false;
      }
      
      return true;
    });

    if (needsSync.length === 0) {
      console.log('📋 No bookings need calendar sync');
      return;
    }

    // SURGICAL FIX 1: Mark as being synced (prevent concurrent attempts)
    const syncingIds = needsSync.map(b => b.id);
    setRecentSyncs(prev => new Set([...prev, ...syncingIds]));
    
    // Clear sync markers after 10 seconds
    setTimeout(() => {
      setRecentSyncs(prev => {
        const newSet = new Set(prev);
        syncingIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }, 10000);

    // PRESERVE: All existing sync logic continues here unchanged
    try {
      const results = await CalendarSyncService.syncBookingsToCalendar(
        effectiveBusinessId, 
        needsSync
      );
      console.log('📅 Calendar sync completed:', results);
      return results;
    } catch (error) {
      console.error('📅 Calendar sync failed:', error);
      // Remove from recent syncs on failure so it can be retried
      setRecentSyncs(prev => {
        const newSet = new Set(prev);
        syncingIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      throw error;
    }
  }, [effectiveBusinessId, recentSyncs]);

  // ==========================================
  // CAPACITY CHECK: Ensure slots are available before creating bookings
  // ==========================================

  const checkAvailability = useCallback(async (bookingData) => {
    if (!effectiveBusinessId) {
      throw new Error('Business context is required for availability checks');
    }

    const bookingDate = bookingData.booking_date || bookingData.bookingDate || bookingData.date;
    const bookingTime = bookingData.time || bookingData.booking_time;
    const serviceName = bookingData.service_name || bookingData.serviceName || bookingData.service_type || null;
    const parsedDuration = parseInt(
      bookingData.duration_minutes || bookingData.durationMinutes || bookingData.duration || 60,
      10
    );
    const duration = Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : 60;

    if (!bookingDate || !bookingTime) {
      throw new Error('Booking date and time are required');
    }

    const bookingDateTime = new Date(`${bookingDate}T${bookingTime}`);
    if (isNaN(bookingDateTime.getTime())) {
      throw new Error('Invalid booking date or time');
    }

    const availability = await ConflictResolutionService.checkTimeSlotAvailability(
      effectiveBusinessId,
      bookingDateTime.toISOString(),
      serviceName,
      duration
    );

    if (!availability.isAvailable) {
      const capacityError = new Error('Selected time is no longer available. Please choose another slot.');
      capacityError.details = availability;
      throw capacityError;
    }

    return availability;
  }, [effectiveBusinessId]);

  // ==========================================
  // SURGICAL FIX 3: Standardized Booking Creation
  // ==========================================

  const createBooking = useCallback(async (bookingData) => {
    console.log('🎯 Creating booking via Lisa backend:', bookingData);
    
    try {
      setLoading(true);
      setError(null);
      
      // SURGICAL FIX 3: Normalize field names to handle variations (don't change data structure)
      const normalizedData = {
        ...bookingData,
        // Handle field name variations defensively
        booking_date: bookingData.booking_date || bookingData.bookingDate || bookingData.date,
        customer_name: bookingData.customer_name || bookingData.customerName,
        customer_phone: bookingData.customer_phone || bookingData.customerPhone,
        customer_email: bookingData.customer_email || bookingData.customerEmail,
        customer_whatsapp: bookingData.customer_whatsapp || bookingData.customerWhatsapp,
        service_name: bookingData.service_name || bookingData.serviceName,
        service_price: bookingData.service_price || bookingData.servicePrice || bookingData.price,
        duration_minutes: bookingData.duration_minutes || bookingData.durationMinutes || bookingData.duration,
        // Preserve all other fields as-is
      };
      
      await checkAvailability(normalizedData);

      const businessProfile = await FirebaseService.getBusinessProfile(effectiveBusinessId);
      const businessPhone =
        businessProfile?.whatsapp_number ||
        businessProfile?.phone_number ||
        businessProfile?.phoneNumber ||
        effectiveBusinessId;

      const payload = {
        business_phone: businessPhone,
        user_phone: normalizedData.customer_phone || normalizedData.customer_whatsapp || '',
        customer_name: normalizedData.customer_name || '',
        service_type: normalizedData.service_type || normalizedData.serviceName || normalizedData.service_name || normalizedData.service || '',
        date: normalizedData.booking_date || normalizedData.date || '',
        time: normalizedData.time || normalizedData.booking_time || '',
        notify_customer: normalizedData.notify_customer !== false
      };

      const response = await fetch(`${LISA_AGENT_API}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Booking failed');
      }

      const booking = {
        id: result.booking_id,
        googleEventId: result.google_event_id,
        calendar_event_id: result.google_event_id,
        customer_name: result.customer_name,
        customerName: result.customer_name,
        customer_phone: payload.user_phone,
        customerPhone: payload.user_phone,
        service_type: result.service_type,
        service_name: result.service_type,
        serviceName: result.service_type,
        booking_date: result.date,
        bookingDate: result.date,
        date: result.date,
        time: result.time,
        status: result.status,
        source: 'dashboard'
      };
      
      console.log('✅ Booking created successfully:', booking.id);
      
      return {
        success: true,
        id: result.booking_id,
        booking_id: result.booking_id,
        google_event_id: result.google_event_id,
        booking
      };
    } catch (error) {
      console.error('❌ Booking creation failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [effectiveBusinessId, checkAvailability]);

  // ==========================================
  // PRESERVE: All existing CRUD operations with enhancements
  // ==========================================

  const getDashboardBusinessPhone = useCallback(async () => {
    const businessProfile = await FirebaseService.getBusinessProfile(effectiveBusinessId);
    return (
      businessProfile?.whatsapp_number ||
      businessProfile?.phone_number ||
      businessProfile?.phoneNumber ||
      effectiveBusinessId
    );
  }, [effectiveBusinessId]);

  const cancelBooking = useCallback(async (bookingData) => {
    try {
      setLoading(true);

      const bookingId = bookingData.id || bookingData.booking_id;
      const businessPhone = await getDashboardBusinessPhone();
      const userPhone =
        bookingData.customerPhone ||
        bookingData.customer_phone ||
        bookingData.customer_whatsapp ||
        bookingData.customerWhatsapp ||
        bookingData.user_phone ||
        bookingData.customerData?.phone ||
        '';

      const response = await fetch(`${LISA_AGENT_API}/api/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_phone: businessPhone,
          user_phone: userPhone,
          reason: 'staff_cancelled'
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Cancellation failed');
      }

      return { success: true, status: 'cancelled' };
    } catch (error) {
      console.error('[useBookings] cancelBooking error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [getDashboardBusinessPhone]);

  const rescheduleBooking = useCallback(async (bookingData) => {
    try {
      setLoading(true);

      const bookingId = bookingData.id || bookingData.booking_id;
      const businessPhone = await getDashboardBusinessPhone();
      const userPhone =
        bookingData.customerPhone ||
        bookingData.customer_phone ||
        bookingData.customer_whatsapp ||
        bookingData.customerWhatsapp ||
        bookingData.user_phone ||
        bookingData.customerData?.phone ||
        '';

      const response = await fetch(`${LISA_AGENT_API}/api/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_phone: businessPhone,
          user_phone: userPhone,
          customer_name: bookingData.customerName || bookingData.customer_name || bookingData.customerData?.name || '',
          service_type: bookingData.serviceType || bookingData.service_type || bookingData.serviceName || bookingData.service_name || '',
          old_date: bookingData.oldDate || bookingData.old_date || bookingData.date || bookingData.booking_date || '',
          old_time: bookingData.oldTime || bookingData.old_time || bookingData.time || bookingData.booking_time || '',
          new_date: bookingData.newDate || bookingData.new_date || '',
          new_time: bookingData.newTime || bookingData.new_time || '',
          reason: 'staff_rescheduled'
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Reschedule failed');
      }

      return {
        success: true,
        booking_id: result.booking_id,
        status: 'rescheduled'
      };
    } catch (error) {
      console.error('[useBookings] rescheduleBooking error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [getDashboardBusinessPhone]);

  const updateBooking = useCallback(async (customerId, bookingId, updateData) => {
    try {
      setLoading(true);
      
      // SURGICAL FIX 3: Apply same field normalization for consistency
      const existingBooking = bookings.find(b => b.id === bookingId || b.booking_id === bookingId) || {};
      const normalizedUpdateData = {
        ...existingBooking,
        ...updateData,
        booking_date: updateData.booking_date || updateData.bookingDate || updateData.date || existingBooking.booking_date || existingBooking.bookingDate || existingBooking.date,
        customer_name: updateData.customer_name || updateData.customerName || existingBooking.customer_name || existingBooking.customerName,
        customer_phone: updateData.customer_phone || updateData.customerPhone || existingBooking.customer_phone || existingBooking.customerPhone,
        customer_email: updateData.customer_email || updateData.customerEmail || existingBooking.customer_email || existingBooking.customerEmail,
        customer_whatsapp: updateData.customer_whatsapp || updateData.customerWhatsapp || existingBooking.customer_whatsapp || existingBooking.customerWhatsapp,
        service_name: updateData.service_name || updateData.serviceName || existingBooking.service_name || existingBooking.serviceName,
        service_price: updateData.service_price || updateData.servicePrice || updateData.price || existingBooking.service_price || existingBooking.servicePrice || existingBooking.price,
        duration_minutes: updateData.duration_minutes || updateData.durationMinutes || updateData.duration || existingBooking.duration_minutes || existingBooking.durationMinutes || existingBooking.duration,
      };

      const oldDate = existingBooking.booking_date || existingBooking.bookingDate || existingBooking.date || '';
      const oldTime = existingBooking.time || existingBooking.booking_time || '';
      const newDate = normalizedUpdateData.booking_date || normalizedUpdateData.bookingDate || normalizedUpdateData.date || '';
      const newTime = normalizedUpdateData.time || normalizedUpdateData.booking_time || '';

      if ((newDate && newDate !== oldDate) || (newTime && newTime !== oldTime)) {
        const result = await rescheduleBooking({
          ...normalizedUpdateData,
          id: bookingId,
          booking_id: bookingId,
          customerId: customerId || existingBooking.customerId,
          oldDate,
          oldTime,
          newDate,
          newTime
        });

        if (!result.success) {
          throw new Error(result.error || 'Booking reschedule failed');
        }

        console.log('✅ Booking rescheduled successfully');
        return result;
      }
      
      const result = await BookingService.updateBooking(
        effectiveBusinessId, 
        customerId, 
        bookingId, 
        normalizedUpdateData
      );
      
      if (result.success) {
        // Real-time listener will handle updates
        console.log('✅ Booking updated successfully');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Booking update failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [effectiveBusinessId, bookings, rescheduleBooking]);

  const deleteBooking = useCallback(async (customerId, bookingId) => {
    const booking = bookings.find(b => b.id === bookingId || b.booking_id === bookingId) || {};
    const result = await cancelBooking({
      ...booking,
      id: bookingId,
      booking_id: bookingId,
      customerId: customerId || booking.customerId
    });

    if (!result.success) {
      setError(result.error);
      throw new Error(result.error || 'Booking cancellation failed');
    }

    console.log('✅ Booking cancelled successfully');
    return result;
  }, [bookings, cancelBooking]);

  // ==========================================
  // PRESERVE: Batch Operations
  // ==========================================
  
  const createMultipleBookings = useCallback(async (bookingsData) => {
    try {
      setLoading(true);
      setError(null);
      
      const results = [];
      
      for (const bookingData of bookingsData) {
        const result = await createBooking(bookingData);
        results.push(result);
        
        if (!result.success) {
          console.warn('Failed to create booking:', result.error);
        }
      }
      
      const successCount = results.filter(r => r.success).length;
      console.log(`Batch booking creation completed: ${successCount}/${bookingsData.length} successful`);
      
      return {
        success: successCount > 0,
        results,
        successCount,
        totalCount: bookingsData.length
      };
      
    } catch (error) {
      console.error('Batch booking creation failed:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [createBooking]);

  // ==========================================
  // PRESERVE: Calendar Operations
  // ==========================================

  const syncAllBookingsToCalendar = useCallback(async (options = {}) => {
    try {
      const { force = false, reason = 'manual' } = options;
      
      if (!calendarIntegration?.hasCalendar && !force) {
        return { 
          success: false, 
          error: 'Calendar integration not set up' 
        };
      }

      console.log(`🔄 Starting bulk calendar sync (${reason}): ${bookings.length} bookings`);
      
      const results = await CalendarSyncService.syncBookingsToCalendar(
        effectiveBusinessId, 
        bookings, 
        { force }
      );
      
      console.log('✅ Bulk calendar sync completed:', results);
      return results;
      
    } catch (error) {
      console.error('❌ Bulk calendar sync failed:', error);
      return { success: false, error: error.message };
    }
  }, [effectiveBusinessId, bookings, calendarIntegration?.hasCalendar]);

  const fetchCalendarEvents = useCallback(async (startDate, endDate) => {
    try {
      if (!calendarIntegration?.hasCalendar) {
        return { success: false, events: [], error: 'Calendar not integrated' };
      }

      const events = await CalendarSyncService.fetchCalendarEvents(
        effectiveBusinessId,
        startDate,
        endDate
      );
      
      setCalendarEvents(events);
      return { success: true, events };
      
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
      return { success: false, events: [], error: error.message };
    }
  }, [effectiveBusinessId, calendarIntegration?.hasCalendar]);

  // ==========================================
  // PRESERVE: Utility Functions
  // ==========================================

  const getCalendarSubscriptionURLs = useCallback(async () => {
    try {
      if (!calendarIntegration?.hasCalendar) {
        return { 
          success: false, 
          error: 'Calendar integration not set up',
          available: false
        };
      }

      const urls = await CalendarSyncService.getCalendarSubscriptionURLs(effectiveBusinessId);
      return { ...urls, available: true };
    } catch (error) {
      console.error('Failed to get calendar subscription URLs:', error);
      return { success: false, error: error.message, available: false };
    }
  }, [effectiveBusinessId, calendarIntegration]);

  const getBookingStats = useCallback(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const thisMonth = todayStr.substring(0, 7);
    
    return {
      total: bookings.length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      pending: bookings.filter(b => b.status === 'pending').length,
      rescheduled: bookings.filter(b => b.status === 'rescheduled').length,
      cancelled: bookings.filter(b => b.status === 'cancelled').length,
      today: bookings.filter(b => b.booking_date === todayStr).length,
      thisMonth: bookings.filter(b => b.booking_date && b.booking_date.startsWith(thisMonth)).length,
      upcoming: bookings.filter(b => new Date(b.booking_date) > today).length,
      totalRevenue: bookings
        .filter(b => b.status === 'confirmed' || b.status === 'rescheduled')
        .reduce((sum, b) => sum + (parseFloat(b.service_price) || 0), 0)
    };
  }, [bookings]);

  const getUpcomingBookings = useCallback((days = 7, limit = 5) => {
    const today = new Date();
    const futureDate = new Date(today.getTime() + (days * 24 * 60 * 60 * 1000));
    
    return bookings
      .filter(booking => {
        if (booking.status === 'cancelled') return false;
        const bookingDate = new Date(booking.booking_date);
        return bookingDate >= today && bookingDate <= futureDate;
      })
      .sort((a, b) => new Date(a.booking_date) - new Date(b.booking_date))
      .slice(0, limit);
  }, [bookings]);

  const getBookingsForDate = useCallback((date) => {
    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date.split('T')[0];
    return bookings.filter(booking => booking.booking_date === dateStr);
  }, [bookings]);

  const getBookingsForDateRange = useCallback((startDate, endDate) => {
    const start = startDate instanceof Date ? startDate.toISOString().split('T')[0] : startDate;
    const end = endDate instanceof Date ? endDate.toISOString().split('T')[0] : endDate;
    
    return bookings.filter(booking => {
      const bookingDate = booking.booking_date;
      return bookingDate >= start && bookingDate <= end;
    });
  }, [bookings]);

  const searchBookings = useCallback((searchTerm) => {
    if (!searchTerm) return bookings;

    const search = searchTerm.toLowerCase();
    return bookings.filter(booking => 
      booking.customer_name?.toLowerCase().includes(search) ||
      booking.service_name?.toLowerCase().includes(search) ||
      booking.customer_phone?.includes(search) ||
      booking.customer_email?.toLowerCase().includes(search) ||
      booking.customer_whatsapp?.includes(search) ||
      booking.notes?.toLowerCase().includes(search)
    );
  }, [bookings]);

  const getBookingsByStatus = useCallback((status) => {
    return bookings.filter(booking => booking.status === status);
  }, [bookings]);

  const getBookingsByCustomer = useCallback((customerId) => {
    return bookings.filter(booking => booking.customerId === customerId);
  }, [bookings]);

  const getBookingsByService = useCallback((serviceName) => {
    return bookings.filter(booking => booking.service_name === serviceName);
  }, [bookings]);

  const filterBookingsByStatus = useCallback((status) => {
    if (status === 'all') return bookings;
    return bookings.filter(booking => booking.status === status);
  }, [bookings]);

  // ==========================================
  // PRESERVE: Analytics Functions
  // ==========================================
  
  const getRevenueStats = useCallback(() => {
    const total = bookings.reduce((sum, booking) => {
      const price = parseFloat(booking.service_price || 0);
      return booking.status !== 'cancelled' ? sum + price : sum;
    }, 0);
    
    const today = new Date().toISOString().split('T')[0];
    const todayRevenue = bookings
      .filter(b => b.booking_date === today && b.status !== 'cancelled')
      .reduce((sum, booking) => sum + parseFloat(booking.service_price || 0), 0);
    
    return {
      total,
      today: todayRevenue,
      average: bookings.length > 0 ? total / bookings.length : 0
    };
  }, [bookings]);

  const getPopularServices = useCallback(() => {
    const serviceCounts = {};
    bookings.forEach(booking => {
      if (booking.service_name && booking.status !== 'cancelled') {
        serviceCounts[booking.service_name] = (serviceCounts[booking.service_name] || 0) + 1;
      }
    });
    
    return Object.entries(serviceCounts)
      .sort(([,a], [,b]) => b - a)
      .map(([service, count]) => ({ service, count }));
  }, [bookings]);

  // ==========================================
  // PRESERVE: Calendar Integration Status Monitoring
  // ==========================================

  const [calendarSetupStatus, setCalendarSetupStatus] = useState('unknown');

  // Monitor calendar integration status changes
  useEffect(() => {
    if (calendarIntegration?.hasCalendar && calendarSetupStatus === 'unknown') {
      setCalendarSetupStatus('integrated');
    } else if (!calendarIntegration?.hasCalendar && calendarSetupStatus === 'integrated') {
      setCalendarSetupStatus('disconnected');
    } else if (calendarIntegration?.hasCalendar && calendarSetupStatus === 'pending') {
      setCalendarSetupStatus('integrated');
      
      console.log('Calendar integration newly established - triggering bulk sync');
      syncAllBookingsToCalendar({ force: false, reason: 'new_integration' });
    }
  }, [calendarIntegration?.hasCalendar, calendarSetupStatus, syncAllBookingsToCalendar]);

  // Expose method to mark calendar setup as pending (call this when starting calendar setup)
  const markCalendarSetupPending = useCallback(() => {
    setCalendarSetupStatus('pending');
  }, []);

  // ==========================================
  // PRESERVE: Customer Management Utilities
  // ==========================================

  const generateCustomerId = useCallback((customerData) => {
    // Generate consistent customer ID based on phone or email
    const identifier = customerData.customer_phone || customerData.customer_email || customerData.customer_whatsapp;
    if (identifier) {
      return `customer_${identifier.replace(/\D/g, '')}`;
    }
    return `customer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const ensureCustomerExists = useCallback(async (customerData) => {
    try {
      const customerId = generateCustomerId(customerData);
      
      // Check if customer already exists
      const existingCustomer = await FirebaseService.getCustomer(effectiveBusinessId, customerId);
      
      if (existingCustomer) {
        console.log('✅ Customer already exists:', customerId);
        return { success: true, customerId, customer: existingCustomer };
      }
      
      // Create new customer
      const newCustomer = {
        id: customerId,
        name: customerData.customer_name,
        phone: customerData.customer_phone,
        email: customerData.customer_email,
        whatsapp: customerData.customer_whatsapp,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const result = await FirebaseService.createCustomer(effectiveBusinessId, customerId, newCustomer);
      
      if (result.success) {
        console.log('✅ Customer created:', customerId);
        return { success: true, customerId, customer: newCustomer };
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Failed to ensure customer exists:', error);
      return { success: false, error: error.message };
    }
  }, [effectiveBusinessId, generateCustomerId]);

  // ==========================================
  // PRESERVE: Advanced Query Functions
  // ==========================================

  const getBookingsByDateRange = useCallback((startDate, endDate, options = {}) => {
    const { status, service, customer, sortBy = 'date', sortOrder = 'asc' } = options;
    
    let filteredBookings = getBookingsForDateRange(startDate, endDate);
    
    if (status) {
      filteredBookings = filteredBookings.filter(b => b.status === status);
    }
    
    if (service) {
      filteredBookings = filteredBookings.filter(b => b.service_name === service);
    }
    
    if (customer) {
      filteredBookings = filteredBookings.filter(b => 
        b.customer_name?.toLowerCase().includes(customer.toLowerCase()) ||
        b.customer_email?.toLowerCase().includes(customer.toLowerCase())
      );
    }
    
    // Sort results
    filteredBookings.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(`${a.booking_date}T${a.time}`);
          bValue = new Date(`${b.booking_date}T${b.time}`);
          break;
        case 'customer':
          aValue = a.customer_name?.toLowerCase() || '';
          bValue = b.customer_name?.toLowerCase() || '';
          break;
        case 'service':
          aValue = a.service_name?.toLowerCase() || '';
          bValue = b.service_name?.toLowerCase() || '';
          break;
        case 'price':
          aValue = parseFloat(a.service_price || 0);
          bValue = parseFloat(b.service_price || 0);
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : -1;
      }
      return aValue > bValue ? 1 : -1;
    });
    
    return filteredBookings;
  }, [getBookingsForDateRange]);

  // ==========================================
  // PRESERVE: Computed Values
  // ==========================================

  const isCalendarIntegrated = calendarIntegration?.hasCalendar || false;
  const calendarStatus = calendarIntegration?.status || 'not_setup';

  // ==========================================
  // RETURN: All existing exports plus surgical enhancements
  // ==========================================

  return {
    // Data - PRESERVED
    bookings,
    calendarEvents,
    calendarIntegration,
    loading,
    error,
    lastUpdated,

    // Calendar Status - PRESERVED
    isCalendarIntegrated,
    calendarSetupURL: calendarIntegration?.setupURL,
    calendarStatus,
    
    // SURGICAL FIX 2: Include updateTrigger for components that need it
    updateTrigger,

    // Actions (Calendar-Aware) - ENHANCED with surgical fixes
    createBooking, // SURGICAL FIX 3: Now uses standardized path with field normalization
    updateBooking, // ENHANCED with field normalization
    deleteBooking, // PRESERVED
    cancelBooking,
    rescheduleBooking,
    createMultipleBookings, // PRESERVED

    // Calendar operations - PRESERVED
    syncToGoogleCalendar, // SURGICAL FIX 1: Now includes deduplication
    syncAllBookingsToCalendar, // PRESERVED
    fetchCalendarEvents, // PRESERVED
    getCalendarSubscriptionURLs, // PRESERVED
    markCalendarSetupPending, // PRESERVED
    calendarSetupStatus, // PRESERVED

    // Queries - PRESERVED
    getBookingStats, // PRESERVED
    getUpcomingBookings, // PRESERVED
    getBookingsForDate, // PRESERVED
    getBookingsForDateRange, // PRESERVED
    getBookingsByDateRange, // PRESERVED
    searchBookings, // PRESERVED
    getBookingsByStatus, // PRESERVED
    getBookingsByCustomer, // PRESERVED
    getBookingsByService, // PRESERVED
    filterBookingsByStatus, // PRESERVED

    // Analytics - PRESERVED
    getRevenueStats, // PRESERVED
    getPopularServices, // PRESERVED

    // Customer Management - PRESERVED
    generateCustomerId, // PRESERVED
    ensureCustomerExists, // PRESERVED

    // SURGICAL FIX 4: Timezone-consistent date utilities for components
    safeParseDateTime, // For components that need to parse dates consistently
    formatBookingDate, // For components that need to format dates consistently  
    createCalendarEvent, // For components that need to create calendar events

    // Utilities - ENHANCED
    refresh: () => {
      console.log('🔄 Bookings refreshed via real-time listener');
      forceUpdate(); // SURGICAL FIX 2: Now includes force update
    }
  };
};
